// src/components/staff-time/StaffPinEntry.jsx
import React, { useState } from 'react';
import { Clock, AlertCircle } from 'lucide-react';
import { getStaffByPin } from '../../services/staffService';
import { styles } from '../../styles/theme';

const StaffPinEntry = ({ onStaffVerified }) => {
  const [pin, setPin] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const handlePinChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError('');

    // Auto-focus next input
    if (value && index < 3) {
      document.getElementById(`pin-${index + 1}`).focus();
    }

    // Auto-submit when all 4 digits entered
    if (index === 3 && value) {
      handleSubmit(newPin.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      document.getElementById(`pin-${index - 1}`).focus();
    }
  };

  const handleSubmit = async (pinValue) => {
    const pinString = pinValue || pin.join('');
    
    if (pinString.length !== 4) {
      setError('Please enter a 4-digit PIN');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const staff = await getStaffByPin(pinString);
      onStaffVerified(staff);
    } catch (err) {
      setError('Invalid PIN. Please try again.');
      setPin(['', '', '', '']);
      document.getElementById('pin-0').focus();
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: '400px', padding: '1rem' }}>
        {/* Logo/Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            width: '80px',
            height: '80px',
            backgroundColor: '#3b82f6',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem'
          }}>
            <Clock style={{ width: '40px', height: '40px', color: 'white' }} />
          </div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: '0 0 0.5rem 0' }}>
            Staff Check-In
          </h1>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
            Enter your 4-digit PIN to continue
          </p>
        </div>

        {/* PIN Entry Card */}
        <div style={styles.card}>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '1.5rem' }}>
            {pin.map((digit, index) => (
              <input
                key={index}
                id={`pin-${index}`}
                type="tel"
                maxLength="1"
                value={digit}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                autoFocus={index === 0}
                style={{
                  width: '60px',
                  height: '60px',
                  fontSize: '1.5rem',
                  textAlign: 'center',
                  border: '2px solid',
                  borderColor: error ? '#ef4444' : (digit ? '#3b82f6' : '#e5e7eb'),
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = digit ? '#3b82f6' : '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
              />
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <AlertCircle style={{ width: '18px', height: '18px', color: '#ef4444', flexShrink: 0 }} />
              <span style={{ color: '#dc2626', fontSize: '0.875rem' }}>{error}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={() => handleSubmit()}
            disabled={isVerifying || pin.some(d => !d)}
            style={{
              width: '100%',
              padding: '1rem',
              backgroundColor: isVerifying || pin.some(d => !d) ? '#9ca3af' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: isVerifying || pin.some(d => !d) ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {isVerifying ? 'Verifying...' : 'Continue'}
          </button>

          {/* Clear Button */}
          <button
            onClick={() => {
              setPin(['', '', '', '']);
              setError('');
              document.getElementById('pin-0').focus();
            }}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: 'transparent',
              color: '#6b7280',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              marginTop: '0.5rem'
            }}
          >
            Clear
          </button>
        </div>

        {/* Help Text */}
        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: '#f0f9ff',
          border: '1px solid #bfdbfe',
          borderRadius: '8px'
        }}>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#1e40af', textAlign: 'center' }}>
            💡 Don't have a PIN? Contact your manager
          </p>
        </div>
      </div>
    </div>
  );
};

export default StaffPinEntry;